import React, { startTransition } from 'react'

//
import {
  Header as HeaderLMR,
  MenuDropdown,
  MenuList,
  MobileNav,
  HeaderContentBlock
} from '../../storybook'
import { HEADER_CONTENT_VARIANT } from '../../storybook/HeaderContentBlock/HeaderContentBlock.const'
import getButtonAction from '../../utils/get-button-action'
import { getMicroCopy } from '../../utils/microcopy'
import { getUrl } from '../../utils/urls'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { useRouter } from 'next/router'
import { useNavigationData } from '@/src/context/NavigationContext'

const Header = ({
  headerData: { id, primaryAction, secondaryAction, mainNavigation, thirdAction },
  minimalHeader,
  minimalHeaderAction,
  slugPrefix = null,
  langSlugs = {},
  marketsSlugs = {},
  allLangSlugs = [],
  hideTopBar = false
}) => {
  const { navigationItems } = useNavigationData()
  const microCopyData = useMicroCopyData()
  const router = useRouter()

  const lang = slugPrefix?.split('-')[0].replaceAll('/', '') || null
  const [activeDropdown, setActiveDropdown] = React.useState(null)
  const [isNavOpen, setIsNavOpen] = React.useState(false)
  const [primeAction, setPrimeAction] = React.useState(() =>
    getButtonAction(primaryAction, slugPrefix, navigationItems, false)
  )

  if (!router || !microCopyData || !slugPrefix || !lang) {
    return null
  }

  React.useEffect(() => {
    startTransition(() => {
      setPrimeAction(getButtonAction(primaryAction, slugPrefix, navigationItems, true))
    })
  }, [primaryAction, slugPrefix, navigationItems])

  React.useEffect(() => setIsNavOpen(false), [router.asPath])

  const handleDropdown = (id) => {
    setActiveDropdown(id)
  }

  const toggleIsNavOpen = () => {
    setIsNavOpen(!isNavOpen)
  }

  const secAction = getButtonAction(secondaryAction, slugPrefix, navigationItems)
  const trdAction = thirdAction ? getButtonAction(thirdAction, slugPrefix, navigationItems) : null

  const mainNavigationItems =
    mainNavigation?.map((item) => {
      const url = item.navigationItem
        ? getUrl({
            navItem: item.navigationItem,
            slugPrefix: slugPrefix,
            navigationItems: navigationItems
          })
        : null
      const navItem = {
        label: item.title,
        dropdown: item.dropdown || null,
        id: item.id,
        navigationItem: url || null
      }

      item.dropdown ? (navItem.id = item.id) : (navItem.url = url)

      return navItem
    }) || []

  const mobileNavigationItems = mainNavigationItems.map((item) => {
    return {
      label: item.label,
      url: item.url ? item.url : null,
      id: `mobile-nav-${item.id}`,
      items: item.dropdown
        ? item.dropdown.contentBlocks.filter((item) => {
            return item.__typename === 'HeaderNavigationBlockModelRecord'
          })
        : null
    }
  })

  const createContentBlock = (blockData) => {
    const {
      __typename,
      id,
      title = '',
      navigationItems: blockNavigationItems = [],
      action = null,
      content = null,
      variant = HEADER_CONTENT_VARIANT.grey
    } = blockData

    switch (__typename) {
      case 'HeaderContentBlockModelRecord':
        return (
          <HeaderContentBlock
            title={title}
            content={content}
            key={id}
            image={blockData.image}
            componentType="summary"
            variant={HEADER_CONTENT_VARIANT[variant]}
            action={{
              label: action?.title || '',
              url: action
                ? getUrl({
                    navItem: action,
                    slugPrefix: slugPrefix,
                    navigationItems: navigationItems
                  })
                : ''
            }}
          />
        )

      case 'HeaderNavigationBlockModelRecord':
        return (
          <MenuList
            title={title}
            items={
              blockNavigationItems?.map((item) => ({
                id: `menu-item-${item.id}`,
                label: item.title || null,
                subtext: item.subtext || null,
                url: getUrl({
                  navItem: item,
                  slugPrefix: slugPrefix,
                  navigationItems: navigationItems
                }),
                icon: item.icon,
                image: item.image,
                background: item.background,
                logo: item.logo
              })) || []
            }
            key={id}
          />
        )

      default:
        return <div key={id}></div>
    }
  }

  const mobileNav = (
    <MobileNav
      key={'mobile-nav'}
      isOpen={isNavOpen}
      actionTitle={getMicroCopy({ key: 'headerNavActionTitle', data: microCopyData[lang] })}
      action={primeAction}
      items={mobileNavigationItems}
      hideTopBar={hideTopBar}
      primaryAction={primeAction}
      secondaryAction={secAction}
      thirdAction={trdAction}
    />
  )

  const dropDowns = [
    ...mainNavigationItems
      .map((item) => {
        if (item.id) {
          return (
            <MenuDropdown
              key={item.id}
              visible={activeDropdown === item.id}
              bottomContent={item.dropdown?.dropdownBottomText || null}
            >
              {item.dropdown?.contentBlocks?.map((block) => createContentBlock(block)) || null}
            </MenuDropdown>
          )
        }
        return null
      })
      .filter((dropdown) => dropdown),
    mobileNav
  ]

  if (minimalHeader) {
    return (
      <HeaderLMR
        logo={{ url: `/${slugPrefix.replace(/\/$/, '')}` }}
        activeDropdown={null}
        handleDropdown={() => {}}
        items={null}
        primaryAction={null}
        toggleNav={toggleIsNavOpen}
        isNavOpen={isNavOpen}
        slugPrefix={slugPrefix}
        langSlugs={langSlugs}
        allLangSlugs={allLangSlugs}
        marketsSlugs={marketsSlugs}
        hideTopBar={hideTopBar}
        secondaryAction={secAction}
        isMinimalHeader={minimalHeader}
        minimalHeaderAction={getButtonAction(minimalHeaderAction, slugPrefix, navigationItems)}
      >
        {[mobileNav]}
      </HeaderLMR>
    )
  }

  return mainNavigationItems.length ? (
    <HeaderLMR
      key={id}
      logo={{ url: `/${slugPrefix.replace(/\/$/, '')}` }}
      activeDropdown={activeDropdown}
      handleDropdown={handleDropdown}
      items={mainNavigationItems}
      toggleNav={toggleIsNavOpen}
      slugPrefix={slugPrefix}
      langSlugs={langSlugs}
      allLangSlugs={allLangSlugs}
      marketsSlugs={marketsSlugs}
      isNavOpen={isNavOpen}
      hideTopBar={hideTopBar}
      primaryAction={primeAction}
      secondaryAction={secAction}
      thirdAction={trdAction}
      minimalHeaderAction={null}
    >
      {dropDowns}
    </HeaderLMR>
  ) : null
}

export default Header
